import { graphql, PageProps } from 'gatsby';
import React from 'react';
import Layout from '../components/layout';
import Seo from '../components/utils/Seo';
import CtaProvider from '../contexts/franchise/cta-provider';
import { extractFaqData } from '../utils/extract-faq-data';
import BlockRenderer from '../components/block-renderer';
import { FranchisePagePageContext, FranchisePagePageData } from './franchise/franchise-page';
import { FranchiseServicePageContext, FranchiseServicePageData } from './franchise/franchise-service';
import { PageTemplate } from '../utils/match-page-template';

/* Each block should output a <section> into the <main> from <Layout> */

export type DefaultTemplatePageData = Queries.DefaultTemplateQuery;

export interface DefaultTemplatePageContext {
    slug: string
    templateName: PageTemplate.DefaultPageBuilder
}

const DefaultTemplate: React.FC<Omit<PageProps<
DefaultTemplatePageData | Pick<FranchisePagePageData, 'cmsFranchisePageBuilder'> | Pick<FranchiseServicePageData, 'cmsFranchiseServicePages'>,
DefaultTemplatePageContext | FranchisePagePageContext | FranchiseServicePageContext
>, 'children'> & {
    children?: React.ReactNode | React.ReactNode[]
}> = ({
    data,
    children,
}) => {
    const pageData = Object.values(data)[0];
    const {
        sections, resolvedSections, title, seo, page_options,
    } = pageData;

    const {
        hide_national_call_center,
        hide_local_cta,
        override_phone_number,
        override_phone_number_descriptor,
    } = page_options || {};

    // Extract FAQ data and generate JSON-LD
    const { faqJsonLd } = extractFaqData(pageData);

    return (
        <>
            {faqJsonLd && (
                <script
                    type="application/ld+json"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(faqJsonLd) || '',
                    }}
                />
            )}
            <CtaProvider
                hideNationalCallCenter={hide_national_call_center}
                hideFranchiseLink={hide_local_cta}
                overridePhoneNumber={override_phone_number}
                overridePhoneNumberDescriptor={override_phone_number_descriptor}
            >
                <Layout>
                    <Seo
                        title={seo?.meta_title || title}
                        description={seo?.meta_description}
                        imageUrl={seo?.meta_image?.url}
                        noindex={seo?.noindex || false}
                    />
                    <BlockRenderer data={sections || resolvedSections} />
                    {children || null}
                </Layout>
            </CtaProvider>
        </>
    );
};

export const query = graphql`
    query DefaultTemplate ($slug: String!) {
        cmsPageBuilderSinglePage(url: { eq: $slug }) {
            title
            url
            seo {
                meta_title
                meta_description
                meta_image {
                    url
                }
            }
            page_options {
                hide_national_call_center
                hide_local_cta
                override_phone_number
                override_phone_number_descriptor
            }
            sections {
                ...BlogrollData
                ...CommunityBannerData
                ...ContactBannerData
                ...FaqsData
                ...FranchiseDisclosureData
                ...GalleryData
                ...HeroData
                ...IconBulletsData
                ...IngredientsDisclosureData
                ...InsurerGridData
                ...MediaContentData
                ...MediacontentFaqsData
                ...FaqsGridData
                ...MultiBlogrollData
                ...PartnerLogosData
                ...QuicklinksData
                ...ResourceDetailData
                ...ReviewGridData
                ...RichtextBlockData
                ...ServicesDetailData
                ...ServicesSliderData
                ...SliderData
                ...SplitTextBannerData
                ...SubnavData
                ...TeamMembersData
                ...TestimonialData
                ...TextBannerData
                ...FormData
                ...AnchorData
            }
        }
    }
`;

export default DefaultTemplate;
